import Tooltip from "rc-tooltip";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import {
  useLazyViewDefinitionByKeyQuery,
  useLazyViewModBusTelemetryRawByPartitionKeyQuery,
  useRegisterDefinitionMutation,
} from "../../../../redux/api/mod-bus/modBusAPI";
import { EPresentation } from "../../../../shared/oversight-core/enums/presentation";
import { ERegisterDataType } from "../../../../shared/oversight-core/enums/register-data-type";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import translate from "../../../../shared/oversight-core/utils/translate";
import styles from "./telemetry-data-configure-modal.module.scss";

interface IRegisterRow {
  registerKey: string;
  rawValue: unknown;
  label: string;
  registerDataType: ERegisterDataType | "";
  presentation: EPresentation | "";
  gain: string;
  offset: string;
  unit: string;
}

interface IProps extends ModelContainerProps {
  closeTelemetryDataConfigureModal: () => void;
  partitionKey: string;
  deviceName: string;
  model: string;
  setIsUpdateCurrentState: React.Dispatch<React.SetStateAction<boolean>>;
  isDefined: boolean;
}

const getNumberOrDefault = (value: string, defaultValue: number) => {
  if (value === "") {
    return defaultValue;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : defaultValue;
};

const getDisplayValue = (row: IRegisterRow) => {
  const rawValue = Number(row.rawValue);

  if (!Number.isFinite(rawValue)) {
    return "-";
  }

  const gain = getNumberOrDefault(row.gain, 1);
  const offset = getNumberOrDefault(row.offset, 0);
  const displayValue = rawValue * gain + offset;
  const formattedValue = Number.isInteger(displayValue)
    ? displayValue.toString()
    : displayValue.toFixed(4).replace(/\.?0+$/, "");

  return `${formattedValue}${row.unit ? ` ${row.unit}` : ""}`;
};

const isConfiguredRegisterRow = (row: IRegisterRow) =>
  row.label.trim() !== "" && row.unit.trim() !== "";

const isPartiallyFilledRow = (row: IRegisterRow) => {
  const fields = [
    row.label.trim(),
    row.registerDataType,
    row.presentation,
    row.gain.trim(),
    row.offset.trim(),
    row.unit.trim(),
  ];
  const filledCount = fields.filter((f) => f !== "").length;
  return filledCount > 0 && filledCount < 6;
};

const getRowFieldErrors = (
  row: IRegisterRow,
): Record<string, { message: string }> => {
  const errors: Record<string, { message: string }> = {};
  const errorMsg = " ";

  if (!row.label.trim())
    errors[`label-${row.registerKey}`] = { message: errorMsg };
  if (!row.registerDataType)
    errors[`registerDataType-${row.registerKey}`] = { message: errorMsg };
  if (!row.presentation)
    errors[`presentation-${row.registerKey}`] = { message: errorMsg };
  if (!row.gain.trim())
    errors[`gain-${row.registerKey}`] = { message: errorMsg };
  if (!row.offset.trim())
    errors[`offset-${row.registerKey}`] = { message: errorMsg };
  if (!row.unit.trim())
    errors[`unit-${row.registerKey}`] = { message: errorMsg };

  return errors;
};

const registerDataTypeOptions: Option[] = Object.values(ERegisterDataType).map(
  (val) => ({
    value: val,
    label: val,
  }),
);

const presentationOptions: Option[] = Object.values(EPresentation).map(
  (val) => ({
    value: val,
    label: translate(val),
  }),
);

const getRegisterRows = (
  rawRegisters: Record<string, unknown>,
  definedRegisters: IRegisterRow[],
): IRegisterRow[] => {
  const definedRegisterMap = new Map(
    definedRegisters.map((register) => [register.registerKey, register]),
  );

  return Object.entries(rawRegisters).map(([registerKey, rawValue]) => {
    const definedRegister = definedRegisterMap.get(registerKey);

    return {
      registerKey,
      rawValue,
      label: definedRegister?.label || "",
      registerDataType: definedRegister?.registerDataType || "",
      presentation: definedRegister?.presentation || "",
      gain: definedRegister?.gain || "",
      offset: definedRegister?.offset || "",
      unit: definedRegister?.unit || "",
    };
  });
};

const TelemetryDataConfigureModal = (props: IProps) => {
  const { setIsUpdateCurrentState, partitionKey, isDefined, ...rest } = props;
  const [registerRows, setRegisterRows] = useState<IRegisterRow[]>([]);
  const [showRegisterError, setShowRegisterError] = useState(false);
  const [showPartialRowErrors, setShowPartialRowErrors] = useState(false);
  const [liveRegisters, setLiveRegisters] = useState<IRegisterRow[]>([]);
  const liveRegistersRef = useRef<IRegisterRow[]>(liveRegisters);

  useEffect(() => {
    liveRegistersRef.current = liveRegisters;
  }, [liveRegisters]);

  const [
    triggerViewModBusTelemetryRaw,
    { isFetching: isFetchingViewModBusTelemetryRaw },
  ] = useLazyViewModBusTelemetryRawByPartitionKeyQuery();
  const [registerDefinition, { isLoading: isLoadingRegisterDefinition }] =
    useRegisterDefinitionMutation();
  const [triggerDefinitionByKey, { isFetching: isFetchingDefinitionByKey }] =
    useLazyViewDefinitionByKeyQuery();

  useEffect(() => {
    if (props.show && partitionKey && isDefined) {
      triggerDefinitionByKey({ partitionKey })
        .unwrap()
        .then((res) => {
          const registers = res.registerDefinition?.registers || [];
          const rows = registers.map((register) => ({
            registerKey: register.registerKey,
            rawValue: "",
            label: register.label,
            registerDataType: register.registerDataType || "",
            presentation: (register.presentation || "") as EPresentation | "",
            gain: String(register.gain ?? ""),
            offset: String(register.offset ?? ""),
            unit: register.unit,
          }));

          setLiveRegisters(rows);
          setShowRegisterError(false);
        })
        .catch(() => {
          setRegisterRows([]);
          setLiveRegisters([]);
          setShowRegisterError(false);
          showErrorMessage("Loading register definition unsuccessful");
        });
    } else {
      setLiveRegisters([]);
    }
  }, [props.show, partitionKey, isDefined, triggerDefinitionByKey]);

  useEffect(() => {
    if (props.show && partitionKey) {
      triggerViewModBusTelemetryRaw({ partitionKey })
        .unwrap()
        .then((res) => {
          const registers = res.telemetry?.registers || {};
          const rows = getRegisterRows(registers, liveRegistersRef.current);

          setRegisterRows(rows);
          setShowRegisterError(false);
        })
        .catch(() => {
          setRegisterRows([]);
          setShowRegisterError(false);
          showErrorMessage("Loading raw telemetry unsuccessful");
        });
    }
  }, [props.show, partitionKey, triggerViewModBusTelemetryRaw]);

  const hasRegisters = useMemo(() => registerRows.length > 0, [registerRows]);
  const configuredRegisterRows = useMemo(
    () => registerRows.filter(isConfiguredRegisterRow),
    [registerRows],
  );
  const hasConfiguredRegister = configuredRegisterRows.length > 0;
  const partialRows = useMemo(
    () => registerRows.filter(isPartiallyFilledRow),
    [registerRows],
  );
  const hasPartialRows = partialRows.length > 0;

  const inputErrors = useMemo(() => {
    if (!showPartialRowErrors) return {};
    let errors: Record<string, { message: string }> = {};
    for (const row of partialRows) {
      errors = { ...errors, ...getRowFieldErrors(row) };
    }
    return errors;
  }, [showPartialRowErrors, partialRows]);

  const updateRegisterRow = (
    registerKey: string,
    field: keyof Omit<IRegisterRow, "registerKey" | "rawValue">,
    value: string,
  ) => {
    setRegisterRows((prevRows) => {
      const nextRows = prevRows.map((row) =>
        row.registerKey === registerKey ? { ...row, [field]: value } : row,
      );

      if (showRegisterError && nextRows.some(isConfiguredRegisterRow)) {
        setShowRegisterError(false);
      }

      if (showPartialRowErrors && !nextRows.some(isPartiallyFilledRow)) {
        setShowPartialRowErrors(false);
      }

      return nextRows;
    });
  };

  const closeModal = () => {
    setRegisterRows([]);
    setLiveRegisters([]);
    setShowRegisterError(false);
    setShowPartialRowErrors(false);
    props.closeTelemetryDataConfigureModal();
  };

  const onSubmit = () => {
    if (hasPartialRows) {
      setShowPartialRowErrors(true);
      return;
    }

    if (!hasConfiguredRegister) {
      setShowRegisterError(true);
      return;
    }

    registerDefinition({
      partitionKey: props.partitionKey,
      deviceName: props.deviceName,
      model: props.model,
      registers: configuredRegisterRows.map((row) => ({
        registerKey: row.registerKey,
        label: row.label.trim(),
        registerDataType: row.registerDataType as ERegisterDataType,
        presentation: row.presentation as EPresentation,
        gain: getNumberOrDefault(row.gain, 1),
        offset: getNumberOrDefault(row.offset, 0),
        unit: row.unit.trim(),
      })),
    })
      .unwrap()
      .then(() => {
        showSuccessMessage("Register configured successfully");
        setIsUpdateCurrentState((ps) => !ps);
        closeModal();
      })
      .catch(() => {
        showErrorMessage("Register configure unsuccessful");
      });
  };

  return (
    <ModalContainer
      {...rest}
      title="Configure Register"
      size="modal-xxl"
      confirmButtonText="Confirm"
      onConfirm={onSubmit}
      isLoading={isLoadingRegisterDefinition}
      disabled={
        !hasRegisters ||
        isFetchingViewModBusTelemetryRaw ||
        isFetchingDefinitionByKey
      }
    >
      <>
        <Row className="mt-4">
          <Col>
            {isFetchingViewModBusTelemetryRaw || isFetchingDefinitionByKey ? (
              <SpinnerModal show={true} withOverlay={false} />
            ) : hasRegisters ? (
              <>
                <div className={styles.registerTableWrap}>
                  <Table className={styles.registerTable}>
                    <colgroup>
                      <col className={styles.registerKeyColumn} />
                      <col className={styles.rawValueColumn} />
                      <col className={styles.labelColumn} />
                      <col className={styles.dataTypeColumn} />
                      <col className={styles.presentationColumn} />
                      <col className={styles.gainColumn} />
                      <col className={styles.offsetColumn} />
                      <col className={styles.unitColumn} />
                      <col className={styles.previewColumn} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>
                          Register
                          <br />
                          Key
                        </th>
                        <th>Raw value</th>
                        <th>Label</th>
                        <th>Data Type</th>
                        <th>Presentation</th>
                        <th>Gain</th>
                        <th>Offset</th>
                        <th>Unit</th>
                        <th>Preview</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registerRows.map((row) => (
                        <tr key={row.registerKey}>
                          <td>
                            <Tooltip
                              placement="top"
                              trigger={["hover"]}
                              overlay={
                                <div className="text-light font-size-12 font-weight-400">
                                  {row.registerKey}
                                </div>
                              }
                            >
                              <div className={styles.registerKey}>
                                {row.registerKey}
                              </div>
                            </Tooltip>
                          </td>
                          <td>{String(row.rawValue)}</td>
                          <td>
                            <AppInput
                              className={styles.tableInput}
                              placeholder="Label"
                              name={`label-${row.registerKey}`}
                              type="text"
                              value={row.label}
                              errors={inputErrors}
                              isRequired={false}
                              onChange={(event) =>
                                updateRegisterRow(
                                  row.registerKey,
                                  "label",
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <AppSelect
                              className={styles.tableInput}
                              placeholder=""
                              name={`registerDataType-${row.registerKey}`}
                              options={registerDataTypeOptions}
                              selectedValue={
                                row.registerDataType
                                  ? {
                                      value: row.registerDataType,
                                      label: row.registerDataType,
                                    }
                                  : undefined
                              }
                              errors={inputErrors}
                              onChangeOption={(selectedOption: Option) => {
                                updateRegisterRow(
                                  row.registerKey,
                                  "registerDataType",
                                  selectedOption.value,
                                );
                              }}
                              menuHeight="130px"
                            />
                          </td>
                          <td>
                            <AppSelect
                              className={styles.tableInput}
                              placeholder=""
                              name={`presentation-${row.registerKey}`}
                              options={presentationOptions}
                              selectedValue={
                                row.presentation
                                  ? {
                                      value: row.presentation,
                                      label: row.presentation,
                                    }
                                  : undefined
                              }
                              errors={inputErrors}
                              onChangeOption={(selectedOption: Option) => {
                                updateRegisterRow(
                                  row.registerKey,
                                  "presentation",
                                  selectedOption.value,
                                );
                              }}
                              menuHeight="150px"
                            />
                          </td>
                          <td>
                            <AppInput
                              className={styles.tableInput}
                              placeholder="Gain"
                              name={`gain-${row.registerKey}`}
                              type="number"
                              value={row.gain}
                              errors={inputErrors}
                              isRequired={false}
                              onChange={(event) =>
                                updateRegisterRow(
                                  row.registerKey,
                                  "gain",
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <AppInput
                              className={styles.tableInput}
                              placeholder="Offset"
                              name={`offset-${row.registerKey}`}
                              type="number"
                              value={row.offset}
                              errors={inputErrors}
                              isRequired={false}
                              onChange={(event) =>
                                updateRegisterRow(
                                  row.registerKey,
                                  "offset",
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <AppInput
                              className={styles.tableInput}
                              placeholder="Unit"
                              name={`unit-${row.registerKey}`}
                              type="text"
                              value={row.unit}
                              errors={inputErrors}
                              isRequired={false}
                              onChange={(event) =>
                                updateRegisterRow(
                                  row.registerKey,
                                  "unit",
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td className={styles.previewValue}>
                            {getDisplayValue(row)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {showRegisterError && !hasConfiguredRegister && (
                  <div className="text-danger font-size-14 mt-2">
                    Fill label and unit for at least one register row.
                  </div>
                )}
                {showPartialRowErrors && hasPartialRows && (
                  <div className="text-danger font-size-14 mt-2">
                    Fill all fields (label, data type, presentation, gain,
                    offset, unit) in each register row, or clear the row
                    entirely.
                  </div>
                )}
              </>
            ) : (
              <div className="container-dash font-size-14 text-muted text-center py-4">
                There are no raw telemetry registers available.
              </div>
            )}
          </Col>
        </Row>
      </>
    </ModalContainer>
  );
};

export default TelemetryDataConfigureModal;
